import * as React from 'react';
import { DefaultButton, Dropdown, Icon, type IDropdownOption, MessageBar, MessageBarType, PrimaryButton, TextField } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { ConfidenceBucket, IChangeDecision, IOrganizationPlan } from '../../../models/Plan';
import { confidenceBucket } from '../../../models/Plan';
import type { IScanResult } from '../../../models/ScanResult';
import { type IResolvedChange, resolveChange } from '../../../plan/PlanSimulator';
import { BUCKET_LABEL, CHANGE_LABEL, changeFrom, changeTitle, changeTo } from './planDisplay';

export interface IChangeCardProps {
  scan: IScanResult;
  plan: IOrganizationPlan;
  resolved: IResolvedChange;
  decision: IChangeDecision | undefined;
  conflicts: string[];
  onDecide: (decision: IChangeDecision) => void;
}

const BUCKET_CLASS: { [bucket in ConfidenceBucket]: string } = {
  high: styles.bucketHigh,
  medium: styles.bucketMedium,
  low: styles.bucketLow,
  needsInput: styles.bucketInput
};

/** One proposed change with Approve / Reject / Edit. */
export default function ChangeCard(props: IChangeCardProps): React.ReactElement {
  const { scan, plan, resolved, decision, conflicts, onDecide } = props;
  const change = resolved.change;
  const bucket: ConfidenceBucket = confidenceBucket(change);
  const status = resolved.status;
  const current: IChangeDecision = decision ?? { status: 'pending' };
  // A needs-input change the admin (or the AI) has already filled in.
  const answered: boolean = !!(current.newName || current.parentPath);

  const [editing, setEditing] = React.useState<boolean>(false);
  const [draft, setDraft] = React.useState<string>('');
  const editable: boolean = change.type === 'renameFile' || change.type === 'moveFolder' || change.type === 'mergeFolders' ||
    change.type === 'fileIntake' || change.type === 'restructure';
  const pathEdit: boolean = change.type === 'moveFolder' || change.type === 'fileIntake' || change.type === 'restructure';

  const startEdit = (): void => {
    setDraft(change.type === 'renameFile'
      ? (resolved.newName ?? scan.files[change.targetId].name)
      : pathEdit ? (resolved.parentPath ?? '') : (resolved.targetFolderId ?? ''));
    setEditing(true);
  };

  const draftDecision = (): IChangeDecision => {
    if (change.type === 'renameFile') { return { ...current, newName: draft.trim() }; }
    if (pathEdit) { return { ...current, parentPath: draft.trim() }; }
    return { ...current, targetFolderId: draft };
  };
  const draftError: string | undefined = editing ? resolveChange(scan, change, draftDecision()).error : undefined;

  const saveEdit = (): void => {
    // A hand-edited value replaces whatever the AI suggested, so its note no longer applies.
    onDecide({ ...draftDecision(), status: 'pending', note: undefined });
    setEditing(false);
  };

  const from: string[] = changeFrom(scan, resolved);
  const to: string | undefined = changeTo(scan, plan, resolved);
  const statusClass: string = status === 'approved' ? styles.changeApproved : status === 'rejected' ? styles.changeRejected : '';

  return (
    <li className={`${styles.change} ${statusClass}`}>
      <div className={styles.changeHeader}>
        <span className={styles.changeType}>{CHANGE_LABEL[change.type]}</span>
        <span className={`${styles.bucket} ${BUCKET_CLASS[bucket]}`}>
          {bucket !== 'needsInput'
            ? `${Math.round(change.confidence * 100)}% · ${BUCKET_LABEL[bucket]}`
            : !answered ? BUCKET_LABEL.needsInput : current.note ? 'AI suggestion' : 'Your choice'}
        </span>
        {status !== 'pending' && (
          <span className={styles.changeStatus}>
            <Icon iconName={status === 'approved' ? 'CheckMark' : 'Cancel'} /> {status === 'approved' ? 'Approved' : 'Rejected'}
          </span>
        )}
      </div>

      <div className={styles.changeTitle}>{changeTitle(scan, resolved)}</div>
      <dl className={styles.changePaths}>
        <dt>From</dt>
        <dd>{from.map(p => <div key={p}>{p || scan.library.name}</div>)}</dd>
        <dt>To</dt>
        <dd>{to !== undefined ? (to || scan.library.name) : <em>Not decided yet</em>}</dd>
      </dl>
      <p className={styles.changeReason}>{change.reason}</p>
      {current.note && <p className={styles.changeNote}>{current.note}</p>}

      {conflicts.map(message => (
        <MessageBar key={message} messageBarType={MessageBarType.warning} isMultiline>{message}</MessageBar>
      ))}

      {editing && (
        <div className={styles.changeEdit}>
          {change.type === 'mergeFolders' ? (
            <Dropdown
              label="Keep this folder and move the others into it"
              selectedKey={draft}
              options={(change.candidateFolderIds ?? []).map<IDropdownOption>(id => ({ key: id, text: scan.folders[id].path }))}
              onChange={(_, option) => option && setDraft(String(option.key))}
            />
          ) : (
            <TextField
              label={change.type === 'renameFile' ? 'New file name'
                : change.type === 'restructure' ? 'Move into folder (path from the library root; missing folders are created)'
                  : change.type === 'fileIntake' ? 'Move into folder (path from the library root, e.g. Finance/Invoices)'
                  : 'Move into folder (path from the library root; leave empty for the top level)'}
              value={draft}
              onChange={(_, value) => setDraft(value ?? '')}
              errorMessage={draftError}
              autoFocus
            />
          )}
          <div className={styles.changeActions}>
            <PrimaryButton text="Save" onClick={saveEdit} disabled={!!draftError} />
            <DefaultButton text="Cancel" onClick={() => setEditing(false)} />
          </div>
        </div>
      )}

      {!editing && (
        <div className={styles.changeActions}>
          {status === 'pending' ? (
            <>
              <PrimaryButton
                text="Approve"
                iconProps={{ iconName: 'CheckMark' }}
                disabled={!!resolved.error}
                title={resolved.error}
                onClick={() => onDecide({ ...current, status: 'approved' })}
              />
              <DefaultButton text="Reject" iconProps={{ iconName: 'Cancel' }} onClick={() => onDecide({ ...current, status: 'rejected' })} />
              {editable && (
                <DefaultButton
                  text={change.needsInput && !resolved.newName && !resolved.parentPath
                    ? (change.type === 'fileIntake' || change.type === 'restructure' ? 'Choose a folder' : 'Enter a name')
                    : 'Modify'}
                  iconProps={{ iconName: 'Edit' }}
                  onClick={startEdit}
                />
              )}
            </>
          ) : (
            <DefaultButton text="Undo decision" iconProps={{ iconName: 'Undo' }} onClick={() => onDecide({ ...current, status: 'pending' })} />
          )}
        </div>
      )}
    </li>
  );
}
